import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
// CRBN vehicle collection fitment navigation
const CRBN_VEHICLE_MODELS = {
  'lexus-parts': [
    ['ES', /\bLexus\s+ES\b/i],
    ['GS', /\bLexus\s+GS\b/i],
    ['GSF', /\bLexus\s+GS\s*F\b|\bLexus\s+GSF\b/i],
    ['IS', /\bLexus\s+IS(?!\s*300\b)(?:\s*\/\s*RC)?\b|\bIS\s*\/\s*RC\b/i],
    ['IS300', /\bLexus\s+IS\s*300\b|\bIS\s*300\b/i],
    ['LC500', /\bLexus\s+LC\s*500\b/i],
    ['RC', /\bLexus\s+RC\b|\bLexus\s+IS\s*\/\s*RC\b|\bIS\s*\/\s*RC\b/i],
  ],
  'honda-parts': [
    ['Accord', /\bHonda\s+Accord\b/i],
    ['Civic', /\bHonda\s+Civic\b/i],
  ],
  'ford-parts': [
    ['F-150', /\bFord\s+F[-\s]?150\b/i],
    ['Mustang', /\bMustang\b/i],
  ],
  'chevrolet-parts': [
    ['Camaro', /\bChevrolet\s+Camaro\b/i],
    ['Silverado 1500', /\bChevrolet\s+Silverado\s*1500\b/i],
  ],
  'dodge-ram-parts': [
    ['Charger / Challenger', /\b(?:Dodge\s+)?(?:Charger|Challenger)\b/i],
    ['Ram 1500 / 2500 / 3500', /\b(?:Dodge\s*\/\s*)?Ram\s+1500\b|\b1500\s*\/\s*2500\s*\/\s*3500\b/i],
  ],
  'infiniti-parts': [
    ['Q50 / Q60', /\bInfiniti\s+Q50\s*\/\s*Q60\b|\bQ50\s*\/\s*Q60\b/i],
  ],
};

const PART_LABELS = new Map([
  ['Steering Wheel', 'Steering Wheels'],
  ['Paddle Shifters', 'Paddle Shifters'],
  ['Interior Trim', 'Interior Trim'],
  ['Air Suspension', 'Suspension'],
  ['Exhaust System', 'Exhaust'],
]);

const PARAMS = {
  model: 'crbn_model',
  year: 'crbn_year',
  part: 'crbn_part',
};

const normalizeText = (value) => String(value || '').trim();

function parseModels(collectionHandle, title) {
  const rules = CRBN_VEHICLE_MODELS[collectionHandle] || [];
  const matches = rules.filter(([, pattern]) => pattern.test(title)).map(([label]) => label);
  return matches.length ? [...new Set(matches)] : ['Other applications'];
}

function parseYear(title) {
  const normalized = title.replace(/[–—]/g, '-');
  const range = normalized.match(/\b((?:19|20)\d{2})\s*-\s*((?:19|20)\d{2})\b/);
  if (range) return `${range[1]}–${range[2]}`;

  const open = normalized.match(/\b((?:19|20)\d{2})\s*\+/);
  if (open) return `${open[1]}+`;

  return 'Fitment confirmation';
}

function explicitYearEnd(title) {
  const normalized = title.replace(/[–—]/g, '-');
  const range = normalized.match(/\b((?:19|20)\d{2})\s*-\s*((?:19|20)\d{2})\b/);
  return range ? Number(range[2]) : null;
}

function parseYears(title, openEndedCeiling = new Date().getFullYear()) {
  const normalized = title.replace(/[–—]/g, '-');
  const range = normalized.match(/\b((?:19|20)\d{2})\s*-\s*((?:19|20)\d{2})\b/);
  if (range) {
    const start = Number(range[1]);
    const end = Number(range[2]);
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => String(start + index));
  }

  const open = normalized.match(/\b((?:19|20)\d{2})\s*\+/);
  if (open) {
    const start = Number(open[1]);
    const end = Math.max(start, openEndedCeiling);
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => String(start + index));
  }

  return ['Fitment confirmation'];
}

function parseYearStart(label) {
  const match = String(label).match(/(?:19|20)\d{2}/);
  return match ? Number(match[0]) : -1;
}

function parsePart(type) {
  const clean = normalizeText(type);
  return PART_LABELS.get(clean) || clean || 'Other parts';
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

function sortModels(values) {
  return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

function sortYears(values) {
  return [...values].sort((a, b) => {
    const startDiff = parseYearStart(b) - parseYearStart(a);
    return startDiff || a.localeCompare(b, undefined, { numeric: true });
  });
}

function sortParts(values) {
  const priority = ['Steering Wheels', 'Paddle Shifters', 'Interior Trim', 'Suspension', 'Exhaust'];
  return [...values].sort((a, b) => {
    const ai = priority.indexOf(a);
    const bi = priority.indexOf(b);
    if (ai !== -1 || bi !== -1) {
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    }
    return a.localeCompare(b);
  });
}

function getSelection() {
  const url = new URL(window.location.href);
  return {
    model: url.searchParams.get(PARAMS.model) || '',
    year: url.searchParams.get(PARAMS.year) || '',
    part: url.searchParams.get(PARAMS.part) || '',
  };
}

function saveSelection(state) {
  const url = new URL(window.location.href);
  Object.entries(PARAMS).forEach(([key, param]) => {
    if (state[key]) url.searchParams.set(param, state[key]);
    else url.searchParams.delete(param);
  });
  history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

function renderButtons(container, values, selected, onSelect) {
  if (!container) return;
  container.replaceChildren();

  const choices = ['All', ...values];
  choices.forEach((label, index) => {
    const value = label === 'All' ? '' : label;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'crbn-fitment-filter__option';
    button.textContent = label;
    button.dataset.value = value;
    button.style.setProperty('--crbn-filter-index', index);
    button.setAttribute('aria-pressed', String(selected === value));
    if (selected === value) button.classList.add('is-active');
    button.addEventListener('click', () => onSelect(value));
    container.append(button);
  });
}

function initFilter(resultsList) {
  const nav = resultsList.querySelector('[data-crbn-vehicle-filter]');
  if (!nav) return;

  const collectionHandle = nav.dataset.collectionHandle;
  if (!CRBN_VEHICLE_MODELS[collectionHandle]) return;

  const cards = [...resultsList.querySelectorAll('[data-crbn-fitment-card]')];
  if (!cards.length) return;

  const cardSignature = cards.map((card) => card.dataset.productId || '').join('|');
  if (nav.dataset.cardSignature === cardSignature && nav.dataset.crbnReady === 'true') return;
  nav.dataset.cardSignature = cardSignature;

  const titles = cards.map((card) => normalizeText(card.dataset.crbnTitle));
  const explicitEnds = titles.map(explicitYearEnd).filter(Number.isFinite);
  const openEndedCeiling = explicitEnds.length ? Math.max(...explicitEnds) : new Date().getFullYear();

  const data = cards.map((card) => {
    const title = normalizeText(card.dataset.crbnTitle);
    const part = parsePart(card.dataset.crbnType);
    const models = parseModels(collectionHandle, title);
    const year = parseYear(title);
    const years = parseYears(title, openEndedCeiling);
    card.dataset.crbnModels = models.join('|');
    card.dataset.crbnYear = year;
    card.dataset.crbnYears = years.join('|');
    card.dataset.crbnPart = part;
    return { card, title, models, year, years, part };
  });

  const allOptions = {
    model: sortModels(unique(data.flatMap((item) => item.models))),
    year: sortYears(unique(data.flatMap((item) => item.years))),
    part: sortParts(unique(data.map((item) => item.part))),
  };

  const state = getSelection();
  if (collectionHandle === 'lexus-parts') {
    if (state.model === 'IS / RC') state.model = 'IS';
    if (state.model === 'LC 500') state.model = 'LC500';
  }
  for (const key of Object.keys(allOptions)) {
    if (!state[key]) continue;
    if (key === 'year' && data.some((item) => item.year === state.year)) continue;
    if (!allOptions[key].includes(state[key])) state[key] = '';
  }

  const itemMatches = (item, keyToIgnore = '') => {
    const modelMatch = keyToIgnore === 'model' || !state.model || item.models.includes(state.model);
    const yearMatch =
      keyToIgnore === 'year' ||
      !state.year ||
      item.year === state.year ||
      item.years.includes(state.year);
    const partMatch = keyToIgnore === 'part' || !state.part || item.part === state.part;
    return modelMatch && yearMatch && partMatch;
  };

  const availableOptions = (key) => {
    const compatible = data.filter((item) => itemMatches(item, key));
    if (key === 'model') return sortModels(unique(compatible.flatMap((item) => item.models)));
    if (key === 'year') return sortYears(unique(compatible.flatMap((item) => item.years)));
    return sortParts(unique(compatible.map((item) => item.part)));
  };

  const reconcileState = () => {
    let changed = true;
    let passes = 0;
    while (changed && passes < 3) {
      changed = false;
      passes += 1;
      for (const key of ['model', 'year', 'part']) {
        if (!state[key]) continue;
        if (!availableOptions(key).includes(state[key])) {
          state[key] = '';
          changed = true;
        }
      }
    }
  };

  reconcileState();

  const countNode = nav.querySelector('[data-crbn-filter-count]');
  const clearButton = nav.querySelector('[data-crbn-filter-clear]');
  const emptyNode = nav.querySelector('[data-crbn-filter-empty]');
  const collapseButton = nav.querySelector('[data-crbn-fitment-collapse]');
  const collapseLabel = nav.querySelector('[data-crbn-fitment-collapse-label]');
  const fitmentSummary = nav.querySelector('[data-crbn-fitment-summary]');
  const mobileFilterQuery = window.matchMedia('(max-width: 749px)');
  const filterOwner = 'crbn-' + (resultsList.getAttribute('section-id') || collectionHandle);
  let drawer = nav.querySelector('[data-crbn-filter-drawer]') ||
    document.querySelector('[data-crbn-filter-drawer][data-crbn-filter-owner="' + filterOwner + '"]');
  let drawerBackdrop = nav.querySelector('[data-crbn-filter-backdrop]') ||
    document.querySelector('[data-crbn-filter-backdrop][data-crbn-filter-owner="' + filterOwner + '"]');

  if (drawerBackdrop && drawerBackdrop.parentElement !== document.body) {
    drawerBackdrop.dataset.crbnFilterOwner = filterOwner;
    document.body.append(drawerBackdrop);
  }
  if (drawer && drawer.parentElement !== document.body) {
    drawer.dataset.crbnFilterOwner = filterOwner;
    document.body.append(drawer);
  }

  const drawerTitle = drawer?.querySelector('[data-crbn-filter-drawer-title]');
  const drawerOptions = drawer?.querySelector('[data-crbn-filter-drawer-options]');
  const drawerClose = drawer?.querySelector('[data-crbn-filter-drawer-close]');
  const triggers = [...nav.querySelectorAll('[data-crbn-filter-trigger]')];
  const grid = cards[0]?.parentElement;
  const filterLabels = { model: 'Model', year: 'Year', part: 'Part type' };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activeDrawerKey = '';
  let drawerCloseTimer = 0;
  let lastDrawerTrigger = null;

  const displayYearLabel = (year) => year === 'Fitment confirmation' ? 'Confirm fitment' : year;

  const setFilterCollapsed = (collapsed) => {
    nav.classList.toggle('is-collapsed', collapsed);
    collapseButton?.setAttribute('aria-expanded', String(!collapsed));
    if (collapseLabel) collapseLabel.textContent = collapsed ? 'Edit filters' : 'Hide filters';
  };

  const updateFitmentSummary = () => {
    if (!fitmentSummary) return;
    const values = [
      state.model,
      state.year ? displayYearLabel(state.year) : '',
      state.part,
    ].filter(Boolean);
    fitmentSummary.textContent = values.length ? values.join(' · ') : 'All applications';
  };

  const renderYearGroups = () => {
    if (!grid) return;
    grid.querySelectorAll('[data-crbn-year-group-heading]').forEach((node) => node.remove());

    const visibleItems = data.filter((item) => !item.card.hidden);
    if (!visibleItems.length) return;

    const years = sortYears(unique(visibleItems.map((item) => item.year)));
    years.forEach((year, index) => {
      const items = visibleItems.filter((item) => item.year === year);
      const heading = document.createElement('li');
      heading.className = `crbn-year-group-heading${index === 0 ? ' is-first' : ''}`;
      heading.dataset.crbnYearGroupHeading = year;
      heading.setAttribute('role', 'presentation');

      const row = document.createElement('div');
      row.className = 'crbn-year-group-heading__link';

      const eyebrow = document.createElement('span');
      eyebrow.className = 'crbn-year-group-heading__eyebrow';
      eyebrow.textContent = 'Year / fitment';

      const title = document.createElement('strong');
      title.textContent = displayYearLabel(year);

      row.append(eyebrow, title);
      heading.append(row);
      grid.append(heading);
      items.forEach((item) => grid.append(item.card));
    });

    data.filter((item) => item.card.hidden).forEach((item) => grid.append(item.card));
    nav.dataset.cardSignature = [...grid.querySelectorAll('[data-crbn-fitment-card]')]
      .map((card) => card.dataset.productId || '')
      .join('|');
  };

  const apply = (updateUrl = true) => {
    let visible = 0;
    data.forEach((item) => {
      const show = itemMatches(item);
      item.card.hidden = !show;
      item.card.setAttribute('aria-hidden', String(!show));
      if (show) {
        visible += 1;
        item.card.classList.add('visible');
      }
    });

    if (countNode) countNode.textContent = `${visible} of ${data.length} products`;
    if (emptyNode) emptyNode.hidden = visible !== 0;
    if (clearButton) clearButton.hidden = !state.model && !state.year && !state.part;
    renderYearGroups();
    if (updateUrl) saveSelection(state);
  };

  const finishDrawerClose = () => {
    if (!drawer) return;
    drawer.hidden = true;
    drawer.classList.remove('is-open');
    if (drawerBackdrop) {
      drawerBackdrop.hidden = true;
      drawerBackdrop.classList.remove('is-open');
    }
    document.documentElement.classList.remove('crbn-fitment-drawer-open');
    activeDrawerKey = '';
    const focusTarget = lastDrawerTrigger;
    lastDrawerTrigger = null;
    focusTarget?.focus({ preventScroll: true });
  };

  const closeDrawer = () => {
    if (!drawer || drawer.hidden) return;
    window.clearTimeout(drawerCloseTimer);
    drawer.classList.remove('is-open');
    drawerBackdrop?.classList.remove('is-open');
    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

    if (reducedMotion.matches) {
      finishDrawerClose();
      return;
    }

    drawerCloseTimer = window.setTimeout(finishDrawerClose, 580);
  };

  const updateTriggers = () => {
    triggers.forEach((trigger) => {
      const key = trigger.dataset.crbnFilterTrigger;
      const value = nav.querySelector('[data-crbn-filter-trigger-value="' + key + '"]');
      if (value) value.textContent = state[key] || 'All';
      trigger.classList.toggle('is-active', Boolean(state[key]));
      trigger.setAttribute('aria-expanded', String(activeDrawerKey === key && drawer && !drawer.hidden));
    });
  };

  const renderActiveDrawer = () => {
    if (!activeDrawerKey || !drawerOptions) return;
    if (drawerTitle) drawerTitle.textContent = filterLabels[activeDrawerKey] || activeDrawerKey;
    renderButtons(
      drawerOptions,
      availableOptions(activeDrawerKey),
      state[activeDrawerKey],
      (value) => {
        state[activeDrawerKey] = value;
        reconcileState();
        draw();
        apply(true);
        closeDrawer();
        if (mobileFilterQuery.matches) setFilterCollapsed(true);
      }
    );
  };

  const openDrawer = (key) => {
    if (!drawer || !drawerOptions || !filterLabels[key]) return;
    window.clearTimeout(drawerCloseTimer);
    activeDrawerKey = key;
    lastDrawerTrigger = triggers.find((trigger) => trigger.dataset.crbnFilterTrigger === key) || null;
    drawer.hidden = false;
    if (drawerBackdrop) drawerBackdrop.hidden = false;
    document.documentElement.classList.add('crbn-fitment-drawer-open');
    renderActiveDrawer();
    updateTriggers();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        drawerBackdrop?.classList.add('is-open');
        drawerClose?.focus({ preventScroll: true });
      });
    });
  };

  const draw = () => {
    updateTriggers();
    updateFitmentSummary();
    if (activeDrawerKey) renderActiveDrawer();
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', () => openDrawer(trigger.dataset.crbnFilterTrigger));
  });
  drawerClose?.addEventListener('click', closeDrawer);
  drawerBackdrop?.addEventListener('click', closeDrawer);
  nav.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });
  drawer?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });

  collapseButton?.addEventListener('click', () => {
    setFilterCollapsed(!nav.classList.contains('is-collapsed'));
  });

  clearButton?.addEventListener('click', () => {
    state.model = '';
    state.year = '';
    state.part = '';
    draw();
    apply(true);
  });

  setFilterCollapsed(mobileFilterQuery.matches);
  draw();
  apply(false);
  nav.hidden = false;
  nav.dataset.crbnReady = 'true';
}

let scanQueued = false;
function scan() {
  if (scanQueued) return;
  scanQueued = true;
  requestAnimationFrame(() => {
    scanQueued = false;
    document.querySelectorAll('results-list').forEach(initFilter);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scan, { once: true });
} else {
  scan();
}

new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
window.addEventListener('popstate', scan);
